import {Injectable} from '@angular/core';
import Dexie, {Table} from 'dexie';
import {
    BlobId,
    LocalScreenshotId,
    ScreenshotDataUrl,
    WorkingMemoryId
} from '@webmate-web-core/shared/types/ids';
import {from, mergeMap, Observable, of, switchMap} from 'rxjs';
import {map, take} from 'rxjs/operators';
import md5 from 'md5';

interface ScreenshotRecord {
    id: LocalScreenshotId,
    data: ScreenshotDataUrl
}

interface BlobRecord {
   id: BlobId,
   data: Blob
}

@Injectable({
    providedIn: 'root'
})
// @ts-ignore
export class IndexedDbService extends Dexie {

    screenshotStore!: Table<ScreenshotRecord, LocalScreenshotId>;
    blobStore!: Table<BlobRecord, BlobId>;

    constructor() {
        super('IndexedDB');
        this.version(1).stores({
            screenshotStore: 'id',
            workingMemoryStore: '++id', // ++id -> auto increment id
            blobStore: 'id',
        });
    }

    addScreenshot(id: LocalScreenshotId, data: ScreenshotDataUrl): Observable<any> {
        return from(this.screenshotStore.put({id, data}));
    }

    getScreenshot(id: LocalScreenshotId) {
        return from(this.screenshotStore.get(id)).pipe(map(record => record?.data))
    }

    removeScreenshot(id: LocalScreenshotId) {
        return from(this.screenshotStore.delete(id));
    }

    addBlob(id: BlobId, blob: Blob): Observable<BlobId> {
        return from(this.blobStore.put({id, data: blob})).pipe(map(() => id));
    }

    // this currently only works for non-in-memory urls (e.g. https:) and not for in-memory urls (e.g. blob:).
    // As the fetch call is controlled by the connect-src CSP you would need to add 'blob:' to the connect-src directive of the csp definition in the nginx.conf.
    // See https://developer.mozilla.org/de/docs/Web/HTTP/Reference/Headers/Content-Security-Policy/connect-src
    addResourceAsBlob(url: string): Observable<BlobId> {
        const blobId: BlobId = md5(url);
        const fetchResource = async (): Promise<Blob> => {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error("Failed to fetch the resource with url: " + url);
            }
            return await response.blob();
        };
        return from(this.getBlob(blobId)).pipe(
            switchMap(blob => {
                if (blob) {
                    return of(blobId);
                }
                return from(fetchResource()).pipe(
                    switchMap(blob => from(this.blobStore.put({id: blobId, data: blob}))),
                );
            }),
            switchMap(_ => of(blobId)),
            take(1),
        );
    }

    getBlob(id: BlobId): Observable<Blob | undefined> {
        return from(this.blobStore.get(id)).pipe(map(record => record?.data));
    }

    removeBlob(id: BlobId) {
        return from(this.blobStore.delete(id));
    }
}
